import React, { type Ref, useEffect, useRef } from 'react';

import Button from '@atlaskit/button/default/button';
import { cssMap } from '@atlaskit/css';
import { useId } from '@atlaskit/ds-lib/use-id';
import Heading from '@atlaskit/heading/heading';
import { Main } from '@atlaskit/navigation-system/layout/main';
import { Panel } from '@atlaskit/navigation-system/layout/panel';
import { PanelSplitter } from '@atlaskit/navigation-system/layout/panel-splitter';
import { Root } from '@atlaskit/navigation-system/layout/root';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';
import { panelEntryPoint } from '@atlassian/entry-point-fixtures/panelEntryPoint';
import {
	PanelActionCloseSmart,
	PanelActionGroup,
	PanelBody,
	PanelContainer,
	PanelHeader,
	PanelProvider,
	PanelsRenderer,
	PanelTitle,
	Trigger,
	useCurrentPanel,
	usePanelManager,
} from '@atlassian/panel-system';
import { PanelContent } from '@atlassian/panel-system/panel-content';

import { RelayEnvironmentWrapper } from '../utils/relay-environment-wrapper';

const styles = cssMap({
	mainContent: {
		flex: 1,
		paddingBlockStart: token('space.300'),
		paddingInlineEnd: token('space.300'),
		paddingBlockEnd: token('space.300'),
		paddingInlineStart: token('space.300'),
		backgroundColor: token('color.background.neutral.subtle'),
	},
	buttonGroup: {
		display: 'flex',
		gap: token('space.100'),
		flexWrap: 'wrap',
	},
});

// Use real entry point from fixtures for prefetching demonstration
const userPanelEntryPoint = panelEntryPoint;
const settingsPanelEntryPoint = panelEntryPoint;
const notificationsPanelEntryPoint = panelEntryPoint;

type UserPanelParams = {
	userId: string;
	userName: string;
};

function CustomUserPanel() {
	const panel = useCurrentPanel<UserPanelParams>();
	const userId = panel.params.userId;
	const userName = panel.params.userName;

	return (
		<PanelContainer>
			<PanelHeader>
				<PanelTitle>User Profile</PanelTitle>
				<PanelActionGroup>
					<PanelActionCloseSmart />
				</PanelActionGroup>
			</PanelHeader>
			<PanelContent>
				<PanelBody>
					<Stack space="space.200">
						<Heading size="small" as="h3">
							{userName}
						</Heading>
						<Text color="color.text.subtle">ID: {userId}</Text>
						<Text size="small">
							This panel was opened via context-based customisation. The component accesses panel
							data cleanly using the useCurrentPanel() hook. Drag the left edge to resize.
						</Text>
					</Stack>
				</PanelBody>
			</PanelContent>
		</PanelContainer>
	);
}

type NotificationsPanelParams = {
	count: number;
};

function NotificationsPanel() {
	const panel = useCurrentPanel<NotificationsPanelParams>();
	const count = panel.params.count;

	return (
		<PanelContainer>
			<PanelHeader>
				<PanelTitle>Notifications</PanelTitle>
				<PanelActionGroup>
					<PanelActionCloseSmart />
				</PanelActionGroup>
			</PanelHeader>
			<PanelContent>
				<PanelBody>
					<Stack space="space.200">
						<Heading size="small" as="h3">
							You have {count} notifications
						</Heading>
						<Text size="small">Drag the left edge to resize this panel.</Text>
					</Stack>
				</PanelBody>
			</PanelContent>
		</PanelContainer>
	);
}

type SettingsPanelParams = {
	category: string;
};

function CustomSettingsPanel() {
	const panel = useCurrentPanel<SettingsPanelParams>();
	const category = panel.params.category;

	return (
		<PanelContainer>
			<PanelHeader>
				<PanelTitle>Settings</PanelTitle>
				<PanelActionGroup>
					<PanelActionCloseSmart />
				</PanelActionGroup>
			</PanelHeader>
			<PanelContent>
				<PanelBody>
					<Stack space="space.200">
						<Heading size="small" as="h3">
							{category || 'General'} Settings
						</Heading>
						<Text size="small">
							This is a custom panel rendering. You can build any UI you want using the panel UI
							components while accessing panel data via context. Drag the left edge to resize.
						</Text>
					</Stack>
				</PanelBody>
			</PanelContent>
		</PanelContainer>
	);
}

function PanelRouter() {
	const panel = useCurrentPanel();
	const { instanceId } = panel;

	if (instanceId.includes('user-panel')) {
		return <CustomUserPanel />;
	}
	if (instanceId.includes('settings-panel')) {
		return <CustomSettingsPanel />;
	}
	if (instanceId.includes('notifications-panel')) {
		return <NotificationsPanel />;
	}
	return null;
}

function CustomPanelRenderingExample() {
	const baseId = useId();
	const { state, manager } = usePanelManager();
	const hasOpenedRef = useRef(false);
	const isPanelOpen = state.activePanel !== null;

	// Open a panel on mount for demonstration
	useEffect(() => {
		if (!hasOpenedRef.current && state.activePanel === null) {
			hasOpenedRef.current = true;
			manager.openPanel({
				instanceId: `${baseId}-user-panel`,
				entryPoint: userPanelEntryPoint,

				params: {
					userId: 'user-123',
					userName: 'Alice Johnson',
				},
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<Root>
			<Main>
				<Box xcss={styles.mainContent}>
					<Stack space="space.300">
						<Heading size="medium">Custom Panel Rendering with Context and Resizing</Heading>
						<Text>
							This example demonstrates the context-based customisation pattern with resizing. Each
							trigger opens a panel, and custom panel components use useCurrentPanel() to access
							their data. Drag the left edge of any panel to resize it.
						</Text>

						<Stack space="space.200">
							<Heading size="small" as="h3">
								Open Panels
							</Heading>
							<Text color="color.text.subtle" size="small">
								Click to open different panel types. Each uses context-based customisation.
							</Text>
							<Box xcss={styles.buttonGroup}>
								<Trigger
									panel={userPanelEntryPoint}
									entryPointProps={{ userId: 'user-123', userName: 'Alice Johnson' }}
									instanceId={`${baseId}-user-panel`}
								>
									{({ ref }) => (
										<Button ref={ref as Ref<HTMLButtonElement>} testId="open-user">
											Open User Profile
										</Button>
									)}
								</Trigger>

								<Trigger
									panel={settingsPanelEntryPoint}
									entryPointProps={{ category: 'Preferences' }}
									instanceId={`${baseId}-settings-panel`}
								>
									{({ ref }) => (
										<Button ref={ref as Ref<HTMLButtonElement>} testId="open-settings">
											Open Settings
										</Button>
									)}
								</Trigger>

								<Trigger
									panel={notificationsPanelEntryPoint}
									entryPointProps={{ count: 5 }}
									instanceId={`${baseId}-notifications-panel`}
								>
									{({ ref }) => (
										<Button ref={ref as Ref<HTMLButtonElement>} testId="open-notifications">
											Open Notifications
										</Button>
									)}
								</Trigger>
							</Box>
						</Stack>
					</Stack>
				</Box>
			</Main>

			{isPanelOpen && (
				<Panel defaultWidth={400} label="Panel">
					<PanelsRenderer>
						<PanelRouter />
					</PanelsRenderer>
					<PanelSplitter label="Resize panel" />
				</Panel>
			)}
		</Root>
	);
}

export default function CustomPanelRenderingExampleWithProvider(): React.JSX.Element {
	return (
		<RelayEnvironmentWrapper>
			<PanelProvider>
				<CustomPanelRenderingExample />
			</PanelProvider>
		</RelayEnvironmentWrapper>
	);
}
